import { ModalReportGeneratorComponent } from 'src/app/modal/modal-report-generator/modal-report-generator.component';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PortalService } from '@api/portal.service';
import { UserMenuService } from '@api/user-menu.service';
import { PortalStateService } from '@states/portal-state.service';
import { SiteStateService } from '@states/site-state.service';
import { UserStateService } from '@states/user-state.service';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ModalAggregatedReportComponent } from 'src/app/modal/modal-aggregated-report/modal-aggregated-report.component';
import { PortalVM, SiteVM } from "@models/backend-generated-models/models";
import { ModalMapComponent } from 'src/app/modal/modal-map/modal-map.component';
import { ModalDashboardComponent } from 'src/app/modal/modal-dashboard/modal-dashboard.component';
import { SiteService } from '@api/site.service';
import { ModalLoggerHealthComponent } from 'src/app/modal/modal-logger-health/modal-logger-health.component';
import { ModalBillUploadComponent } from 'src/app/modal/modal-bill-upload/modal-bill-upload.component';
import { ModalImportComponent } from 'src/app/modal/modal-import/modal-import.component';
import { BillUploadQueueService } from '@states/bill-upload-queue.service';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavbarComponent {
    portals$ = this.portalService.getPortals$()
        .pipe(
            take(1),
            tap(
                (portals: PortalVM[]) => {

                    const portalId = localStorage.getItem('portalId');

                    if (!portalId) {
                        this.setDefaultPortal(portals[0]);
                        return;
                    }

                    const portal = portals.find(p => p.portalId === +portalId);

                    if (!portal) {
                        this.setDefaultPortal(portals[0]);
                        return;
                    }

                    this.portalStateService.selectedPortalSource.next(portal);
                }
            )
        );

    selectedPortal$ = this.portalStateService.selectedPortal$.pipe(filter(q => !!q));

    sites$ = this.selectedPortal$
        .pipe(
            switchMap(p =>
                this.portalService.getSitesForPortal$(p!.portalId)
            ), tap(sites => {

                const siteId = localStorage.getItem('siteId');

                if (!siteId) {
                    this.setDefaultSite(sites[0]);
                    return;
                }

                const site = sites.find(s => s.siteId === +siteId);

                if (!site) {
                    this.setDefaultSite(sites[0]);
                    return;
                }

                this.siteStateService.selectedSiteSource.next(site);
            })
        )

    selectedSite$ = this.siteStateService.selectedSite$;

    // bill uploads parked awaiting an overwrite confirmation — drives the menu badge
    pendingConfirmation$ = this.billQueue.pendingConfirmationCount$;

    portalSearch$ = new BehaviorSubject<string>('');

    filteredPortals$ = combineLatest([this.portals$, this.portalSearch$]).pipe(
        map(([portals, search]) => {
            if (!search) return portals;
            const lowSearch = search.toLowerCase();
            return portals.filter(p => p.name?.toLowerCase().includes(lowSearch));
        })
    );

    logos$ = this.selectedSite$
        .pipe(
            switchMap(
                site => {
                    if (site) {
                        return this.portalService.getLogos$(site.siteId);
                    } else {
                        return of(null);
                    }
                }
            )
        );

    userManuelUrl$ = this.userMenuService.getUserManualURL$();
    aggregatedReportUrl$ = this.userMenuService.getAggregatedReportURL$().pipe(shareReplay(1));

    isSuperAdmin$ = this.userStateService.currentUser$.pipe(
        map(user => {
            if (user && user.role) {
                return user.role.toLowerCase().includes('superadmin');
            }
            return false;
        }),
        shareReplay(1)
    );

    loggerAlertCount$ = combineLatest([this.selectedSite$, this.isSuperAdmin$]).pipe(
        filter(([site, isSuperAdmin]) => site !== null),
        switchMap(([site, isSuperAdmin]) => {
            if (!isSuperAdmin) {
                return of([]);
            }
            return this.siteService.getLoggerHealth$(site!.siteId!).pipe(
                catchError(err => {
                    console.error('[Navbar Logger Health Count] API call failed:', err);
                    return of([]); // Return empty list safely on API failure
                })
            );
        }),
        map(data => {
            if (data && data.length > 0) {
                const problemStatuses = ['late', 'offline', 'processing stuck', 'import error', 'aggregation delayed', 'unmapped', 'frozen value', 'aggregation missing'];
                const issues = data.filter(l => {
                    const status = (l.currentStatus || '').trim().toLowerCase();
                    return problemStatuses.includes(status);
                });
                const uniqueGateways = new Set(issues.map(l => {
                    if (l.gatewayKey && l.gatewayKey.trim() !== '') return l.gatewayKey.trim();
                    if (l.sourceDeviceKey && l.sourceDeviceKey.trim() !== '') return l.sourceDeviceKey.trim();
                    return `fallback-${l.siteID || 0}-${l.trvID || 0}-${l.healthStatusID}`;
                }));
                return uniqueGateways.size;
            }
            return 0;
        }),
        shareReplay(1)
    );

    constructor(
        private portalService: PortalService,
        private router: Router,
        public dialog: MatDialog,
        private userStateService: UserStateService,
        private portalStateService: PortalStateService,
        private siteStateService: SiteStateService,
        private userMenuService: UserMenuService,
        private siteService: SiteService,
        private billQueue: BillUploadQueueService,
    ) {
    }

    onSelectedPortalChange(portal: PortalVM) {
        this.portalStateService.selectedPortalSource.next(portal);
    }

    onSelectedSiteChange(site: SiteVM) {
        this.siteStateService.selectedSiteSource.next(site);
    }

    openDashboardReport() {
        this.dialog.open(ModalDashboardComponent, {
            width: '1200px',
            maxWidth: '95vw',
            data: {
                siteId: this.siteStateService.selectedSiteSource.getValue()?.siteId
            }
        });
    }

    openAggregatedReport() {
        this.dialog.open(ModalAggregatedReportComponent, {
            width: '90vw',
            maxWidth: '1200px',
            maxHeight: '90vh',
            data: {
                url: this.aggregatedReportUrl$,
                portalId: this.portalStateService.selectedPortalSource.getValue()?.portalId
            },
        });
    }

    openMap() {
        this.dialog.open(ModalMapComponent, {
            data: {
                portalId: this.portalStateService.selectedPortalSource.getValue()?.portalId,
            }
        })
    }

    openLoggerHealthModal() {
        this.dialog.open(ModalLoggerHealthComponent, {
            width: '95vw',
            maxWidth: '1350px',
            panelClass: 'logger-health-dialog-panel'
        });
    }

    // Import meter data is SuperAdmin only (isSuperAdmin$ above). The API refuses anyone else; this just hides the menu item.
    openImportModal() {
        this.dialog.open(ModalImportComponent, {
            width: '1000px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            // an import can run for a minute or two; a stray backdrop click mustn't lose the review
            disableClose: true
        });
    }

    openBillUploadModal() {
        this.dialog.open(ModalBillUploadComponent, {
            width: '760px',
            maxWidth: '95vw',
            // bill upload runs a long extraction — don't let a stray backdrop
            // click or Esc close it mid-flight; only the in-modal × closes it
            disableClose: true
        });
    }

    onPortalSearch(event: Event) {
        const target = event.target as HTMLInputElement;
        this.portalSearch$.next(target.value);
    }

    clearPortalSearch(input?: HTMLInputElement) {
        if (input) input.value = '';
        this.portalSearch$.next('');
        if (input) input.focus();
    }

    private setDefaultPortal(portal: PortalVM) {
        this.portalStateService.selectedPortalSource.next(portal);

        localStorage.setItem('portalId', portal.portalId!.toString());
    }

    private setDefaultSite(site: SiteVM) {
        this.siteStateService.selectedSiteSource.next(site);

        localStorage.setItem('siteId', site.siteId!.toString());
    }

    onLogout() {
        localStorage.clear();
        this.userStateService.currentUserSource.next(null);
        this.router.navigate(['login']);
    }


    openReportGenerator() {
        this.dialog.open(ModalReportGeneratorComponent, {
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '95vh',
            panelClass: 'report-generator-dialog-panel'
        });
    }

}
